import { useState, type KeyboardEvent } from 'react';

import type { StaffOption } from '../../api/staff-options';

import { useStaffOptions } from './use-staff-options';

/** Open/search/keyboard state for a people picker. `query` is what the RD has
 *  typed, and null until they type, so the input can show the current name. */
export function usePeoplePicker(onPick: (user: StaffOption) => void) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(0);
  const options = useStaffOptions(query ?? '', open);
  const users = options.data ?? [];
  const status = options.isError ? 'failed' : options.isPending ? 'loading' : 'ready';

  const close = () => {
    setOpen(false);
    setQuery(null);
  };
  const openList = () => {
    setOpen(true);
    setActive(0);
  };
  const pick = (user: StaffOption) => {
    onPick(user);
    close();
  };
  const search = (text: string) => {
    setQuery(text);
    openList();
  };
  const move = (step: number) =>
    setActive((i) => Math.max(0, Math.min(i + step, users.length - 1)));
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
    if (e.key === 'ArrowDown' && !open) openList();
    else if (e.key === 'ArrowDown') move(1);
    if (e.key === 'ArrowUp') move(-1);
    if (e.key === 'Enter' && open && users[active]) {
      e.preventDefault();
      pick(users[active]);
    }
  };

  return { query, open, users, status, active, pick, openList, search, close, onKeyDown } as const;
}
